// Reminders — watches the notes list every minute (while the tab is visible)
// and fires an in-app toast + a native browser notification when a reminder's
// time has arrived. Recurring reminders (daily/weekly/monthly/yearly) advance
// their stored timestamp to the next occurrence so the same note can fire
// again next cycle without the user re-setting it.

import { useEffect } from "react";
import { toast } from "sonner";

const FIRED_KEY = "keepflow.remindersFired";    // { [noteId]: ISO-timestamp of last fire }
const CHECK_EVERY_MS = 60_000;                   // once per minute is plenty

function loadFired() {
  try { return JSON.parse(localStorage.getItem(FIRED_KEY) || "{}"); } catch { return {}; }
}
function saveFired(map) {
  try { localStorage.setItem(FIRED_KEY, JSON.stringify(map)); } catch {}
}

/** Shift an ISO reminder forward by one recurrence period from "now". */
export function advanceReminder(iso, recurrence) {
  if (!iso || !recurrence || recurrence === "none") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // Keep ticking until the next occurrence is in the future, so a reminder
  // that was missed for N cycles still only fires once (not N times).
  const now = new Date();
  while (d <= now) {
    switch (recurrence) {
      case "daily":   d.setDate(d.getDate() + 1); break;
      case "weekly":  d.setDate(d.getDate() + 7); break;
      case "monthly": d.setMonth(d.getMonth() + 1); break;
      case "yearly":  d.setFullYear(d.getFullYear() + 1); break;
      default: return null;
    }
  }
  return d.toISOString();
}

async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  try {
    const perm = await Notification.requestPermission();
    return perm === "granted";
  } catch { return false; }
}

/**
 * Hook: polls `notes` every minute, fires toast + Notification for any note
 * whose `reminder` <= now. For recurring notes it also calls `onReschedule`
 * so KeepApp can patch the note's reminder forward to the next occurrence.
 */
export function useReminders(notes, { onReschedule } = {}) {
  useEffect(() => {
    if (!notes?.length) return;
    let cancelled = false;
    let fired = loadFired();

    const tick = async () => {
      if (cancelled) return;
      const now = Date.now();
      for (const n of notes) {
        if (!n.reminder || n.trashed || n.archived) continue;
        const t = new Date(n.reminder).getTime();
        if (Number.isNaN(t) || t > now) continue;
        // Skip if we've already fired THIS exact occurrence.
        if (fired[n.id] === n.reminder) continue;

        fired[n.id] = n.reminder;
        saveFired(fired);

        const title = n.title?.trim() || "Reminder";
        const body = (n.content || "").replace(/<[^>]+>/g, " ").trim().slice(0, 120) || "Open Mango Notes";
        toast.message(`🔔 ${title}`, { description: body, duration: 8000 });

        const granted = await ensureNotificationPermission();
        if (granted && document.visibilityState !== "visible") {
          try {
            new Notification(title, { body, tag: `keepflow-${n.id}`, badge: "/logo192.png" });
          } catch {}
        }

        if (n.reminder_recurrence && n.reminder_recurrence !== "none") {
          const next = advanceReminder(n.reminder, n.reminder_recurrence);
          if (next && onReschedule) onReschedule(n.id, next);
        }
      }
    };

    // Kick immediately so a reminder that lapsed while the tab was closed
    // fires on open, then settle into the minute-ly cadence.
    tick();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") tick();
    }, CHECK_EVERY_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [notes, onReschedule]);
}

/** Count reminders due today (used by the KeepApp "Due today" banner). */
export function dueTodayCount(notes) {
  if (!notes?.length) return 0;
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(); end.setHours(23, 59, 59, 999);
  return notes.filter((n) => {
    if (!n.reminder || n.trashed || n.archived) return false;
    const t = new Date(n.reminder).getTime();
    return t >= start.getTime() && t <= end.getTime();
  }).length;
}
